import './styles/index.scss';

import { Chip, Grid, Icon, Paper } from '@mui/material';
import { GridActionsCellItem } from '@mui/x-data-grid';
import { RoleInterface } from '@thro-types/access-handler';

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useRoleMutation, useRolesQuery } from 'api/hooks/accessHandler';
import { CreateButton, TypedGridColumns } from 'app/components';
import { ConfirmationDialog } from 'app/components/ConfirmationDialog';
import { DataGrid } from 'app/components/DataGrid';
import { Modal } from 'app/components/Modal';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import usePageHeading from 'utils/hooks/usePageHeading';

import { RoleModal } from '../RoleModal';

export function RoleSettings() {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [disabledCancel, setDisabledCancel] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [currentlyEditingRole, setCurrentlyEditingRole] = useState<RoleInterface | undefined>(
    undefined
  );

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 25,
    page: 0
  });

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  usePageHeading({
    heading: t(translations.pages.roles.title()),
    headingProps: { back: location.state?.back ?? `/settings/access-handler` }
  });

  const {
    data: rolesData,
    isLoading: rolesLoading,
    isError: rolesError
  } = useRolesQuery({
    params: {
      limit: paginationModel.pageSize,
      offset: paginationModel.page * paginationModel.pageSize
    }
  });

  const { deletion } = useRoleMutation();

  const handlePaginationChange = useCallback(pagination => {
    setPaginationModel(pagination);
  }, []);

  const cancelRoleCreation = (_event?, reason?) => {
    if (disabledCancel || (reason && reason === 'backdropClick')) {
      return;
    }
    setCurrentlyEditingRole(undefined);
    setDisabledCancel(true);
    setCreateModalOpen(false);
    setDisabledCancel(false);
  };

  const handleDelete = async () => {
    try {
      if (!currentlyEditingRole) {
        return;
      }
      await deletion.mutateAsync(currentlyEditingRole.id);
      dispatch(AlertActions.success(t(translations.pages.roles.alerts.deleteSuccess())));
    } catch (err) {
      dispatch(AlertActions.error(t(translations.pages.roles.alerts.deleteFailed())));
    } finally {
      setCurrentlyEditingRole(undefined);
    }
  };

  const rowActions = useCallback(
    params => {
      return [
        <GridActionsCellItem
          icon={
            <Icon
              className="far fa-edit"
              style={{
                width: 'auto',
                color: 'lightblue'
              }}
            />
          }
          label={t(translations.common.edit())}
          onClick={() => {
            setCurrentlyEditingRole(params.row);
            setCreateModalOpen(true);
          }}
          showInMenu
        />,
        <GridActionsCellItem
          icon={
            <Icon
              className="far fa-trash-alt"
              style={{
                width: 'auto',
                color: 'red'
              }}
            />
          }
          label={t(translations.common.delete())}
          onClick={() => {
            setCurrentlyEditingRole(params.row);
            setDeleteModalOpen(true);
          }}
          showInMenu
        />
      ];
    },
    [t]
  );

  const columns = useMemo((): TypedGridColumns<RoleInterface> => {
    return [
      {
        field: 'id',
        flex: 0.5,
        headerName: t(translations.common.id())
      },
      {
        field: 'name',
        flex: 2,
        headerName: t(translations.pages.roles.model.name())
      },
      {
        field: 'entitlementGroups',
        flex: 3,
        headerName: t(translations.pages.roles.model.entitlementGroups()),
        renderCell: params => (
          <Grid container style={{ height: 'fit-content' }}>
            {params.row.entitlementGroups?.map(ent => (
              <Chip key={ent.id} label={ent.name} style={{ margin: '0.25em' }} />
            ))}
          </Grid>
        )
      },
      {
        field: 'accessPermissions',
        flex: 4,
        headerName: t(translations.pages.roles.model.permissions()),
        renderCell: params => {
          return (
            <Grid container style={{ maxHeight: '200px', overflowY: 'auto' }} paddingY="0.5em">
              {params.row.accessPermissions?.map(accPerm => (
                <Chip key={accPerm.id} label={accPerm.permission} style={{ margin: '0.25em' }} />
              ))}
            </Grid>
          );
        }
      },
      {
        flex: 0.5,
        minWidth: 100,
        headerName: t(translations.common.actions()),
        field: 'actions',
        type: 'actions',
        getActions: rowActions
      }
    ];
  }, [rowActions, t]);

  if (rolesError) {
    dispatch(AlertActions.error(`${t(translations.pages.roles.alerts.errorLoading())}`));
    navigate('/', { replace: true });
    return;
  }

  return (
    <Grid container>
      <ConfirmationDialog
        open={deleteModalOpen}
        title={t(translations.pages.roles.deleteConfirm.title())}
        onClose={() => {
          setDeleteModalOpen(false);
        }}
        onConfirm={() => {
          setDeleteModalOpen(false);
          handleDelete();
        }}
      >
        {t(translations.pages.roles.deleteConfirm.message())}
      </ConfirmationDialog>
      <Modal
        open={createModalOpen}
        onClose={cancelRoleCreation}
        headline={t(translations.pages.roles.createRole())}
        onCancel={cancelRoleCreation}
        disabledCancel={disabledCancel}
      >
        <RoleModal
          onCancel={cancelRoleCreation}
          closeHandler={cancelRoleCreation}
          setDisabledCancel={setDisabledCancel}
          role={currentlyEditingRole}
        />
      </Modal>
      <Grid container justifyContent={'flex-end'}>
        <Grid item xs={12} display={'flex'} justifyContent={'flex-end'} marginBottom={'1em'}>
          <CreateButton onClick={() => setCreateModalOpen(true)}>
            {t(translations.pages.roles.createRole())}
          </CreateButton>
        </Grid>
      </Grid>
      <Grid container component={Paper} style={{ height: '75vh' }}>
        <DataGrid
          getEstimatedRowHeight={() => 100}
          getRowHeight={() => 'auto'}
          style={{ height: '100%', width: '100%' }}
          disableColumnFilter
          disableRowSelectionOnClick
          columns={columns}
          rows={rolesData?.data || []}
          rowCount={rolesData?.pagination.count || 0}
          paginationMode="server"
          paginationModel={{
            page: paginationModel.page,
            pageSize: paginationModel.pageSize
          }}
          onPaginationModelChange={pagModel => {
            handlePaginationChange(pagModel);
          }}
          loading={rolesLoading}
        />
      </Grid>
    </Grid>
  );
}
